import { useEffect, useMemo, useRef, useState } from 'react'
import type { AnimationEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Cloud, CloudCog, X } from 'lucide-react'
import { Button } from '../Button/Button'
import {
  actions,
  backdrop,
  body,
  breadcrumb as breadcrumbClass,
  closeButton,
  content,
  rail,
  saveState,
  shell,
  title,
  titleStack,
  topbar,
} from './FocusEditor.css'

export type FocusSaveState = 'saved' | 'saving' | 'dirty'

let openFocusEditors = 0

export type FocusEditorProps = {
  open: boolean
  onClose: () => void
  title: string
  breadcrumb: string
  rail?: ReactNode
  children: ReactNode
  saveState?: FocusSaveState
  primaryLabel?: string
  primaryRightIcon?: ReactNode
  onPrimaryAction?: () => void
  primaryDisabled?: boolean
}

function isTopmostDialog(dialog: HTMLElement) {
  const dialogs = Array.from(document.querySelectorAll<HTMLElement>('[aria-modal="true"]'))
  return dialogs.at(-1) === dialog
}

function getSaveLabel(state: FocusSaveState) {
  if (state === 'saving') return 'Salvando'
  if (state === 'dirty') return 'Alterações locais'
  return 'Salvo'
}

export function FocusEditor({
  open,
  onClose,
  title: titleText,
  breadcrumb,
  rail: railContent,
  children,
  saveState: state = 'saved',
  primaryLabel,
  primaryRightIcon,
  onPrimaryAction,
  primaryDisabled,
}: FocusEditorProps) {
  const dialogRef = useRef<HTMLElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [keepMounted, setKeepMounted] = useState(open)
  const currentContent = useMemo(
    () => ({
      titleText,
      breadcrumb,
      railContent,
      children,
      state,
      primaryLabel,
      primaryRightIcon,
      onPrimaryAction,
      primaryDisabled,
    }),
    [
      breadcrumb,
      children,
      onPrimaryAction,
      primaryDisabled,
      primaryLabel,
      primaryRightIcon,
      railContent,
      state,
      titleText,
    ],
  )
  const [closingContent, setClosingContent] = useState(currentContent)
  const visibleContent = open ? currentContent : closingContent
  const currentContentRef = useRef(currentContent)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    currentContentRef.current = currentContent
    onCloseRef.current = onClose
  }, [currentContent, onClose])

  function requestClose() {
    setClosingContent(currentContentRef.current)
    onCloseRef.current()
  }

  useEffect(() => {
    if (!open) return

    function handleKeyDown(event: KeyboardEvent) {
      const dialog = dialogRef.current
      if (!dialog || !isTopmostDialog(dialog)) return

      if (event.key === 'Escape') {
        setClosingContent(currentContentRef.current)
        onCloseRef.current()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => !element.hasAttribute('aria-hidden'))

      const first = focusable[0]
      const last = focusable.at(-1)
      if (!first || !last) return

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    const previousOverflow = document.body.style.overflow
    openFocusEditors += 1
    document.body.dataset.focusEditorOpen = 'true'
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)
    closeButtonRef.current?.focus()

    return () => {
      openFocusEditors = Math.max(0, openFocusEditors - 1)
      if (openFocusEditors === 0) {
        delete document.body.dataset.focusEditorOpen
      }
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  const shouldRender = open || keepMounted

  function handleBackdropAnimationStart(event: AnimationEvent<HTMLDivElement>) {
    if (!open || event.target !== event.currentTarget) return
    setKeepMounted(true)
  }

  function handleBackdropAnimationEnd(event: AnimationEvent<HTMLDivElement>) {
    if (open || event.target !== event.currentTarget) return
    setKeepMounted(false)
  }

  if (!shouldRender) return null

  const editorState = open ? 'open' : 'closing'

  return createPortal(
    <div
      className={backdrop}
      data-state={editorState}
      role="presentation"
      onAnimationStart={handleBackdropAnimationStart}
      onAnimationEnd={handleBackdropAnimationEnd}
      onClick={requestClose}
    >
      <section
        ref={dialogRef}
        className={shell}
        data-state={editorState}
        role="dialog"
        aria-modal="true"
        aria-hidden={!open}
        aria-label={visibleContent.titleText}
        onClick={(event) => {
          event.stopPropagation()
        }}
      >
        <header className={topbar}>
          <button
            ref={closeButtonRef}
            type="button"
            className={closeButton}
            onClick={requestClose}
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
          <div className={titleStack}>
            <span className={breadcrumbClass}>{visibleContent.breadcrumb}</span>
            <h1 className={title}>{visibleContent.titleText}</h1>
          </div>
          <div className={actions}>
            <span className={saveState}>
              {visibleContent.state === 'saving' ? <CloudCog size={15} /> : <Cloud size={15} />}
              {getSaveLabel(visibleContent.state)}
            </span>
            {visibleContent.primaryLabel ? (
              <Button
                onClick={visibleContent.onPrimaryAction}
                disabled={visibleContent.primaryDisabled}
                rightIcon={visibleContent.primaryRightIcon}
              >
                {visibleContent.primaryLabel}
              </Button>
            ) : null}
          </div>
        </header>
        <div className={body} data-rail={visibleContent.railContent ? 'true' : 'false'}>
          {visibleContent.railContent ? (
            <aside className={rail}>{visibleContent.railContent}</aside>
          ) : null}
          <div className={content}>{visibleContent.children}</div>
        </div>
      </section>
    </div>,
    document.body,
  )
}
